import React from 'react';
import { Form, Input, Select, Switch, Space, Divider } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { CreateRoleModalBody, CreateRoleModalFooter } from '@/pages/inside/tenant/style';
import { PROJECT_TYPE_OPTIONS } from '@/hooks/projects/useCreateProjectModal';
import StatusBadge from '@/components/ui/StatusBadge';

export default function ProjectTemplateModal({
    open,
    onCancel,
    onSubmit,
    form,
    isSaving,
    isEdit = false,
    isView = false,
}) {
    const selectedTypes = Form.useWatch('allowedProjectTypes', form) || [];
    const [codeLen, setCodeLen] = React.useState(0);
    const [nameLen, setNameLen] = React.useState(0);
    const [descLen, setDescLen] = React.useState(0);
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                const vals = form.getFieldsValue();
                setCodeLen((vals.code || '').length);
                setNameLen((vals.name || '').length);
                setDescLen((vals.description || '').length);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [open, form]);

    const submissionModeValue = Form.useWatch('submissionMode', form) || 'formal';
    const submissionLabel =
        submissionModeValue === 'formal'
            ? 'Formal Client Submission'
            : submissionModeValue === 'memo'
                ? 'Client Review Memo'
                : submissionModeValue === 'internal'
                    ? 'Internal Workspace Approval'
                    : 'No Client Approval';

    return (
        <AppModal
            size="md"
            title={isView ? 'View Project Template' : isEdit ? 'Edit Project Template' : 'Create Project Template'}
            open={open}
            onCancel={onCancel}
            footer={null}
            noBodyPadding
            styles={{ body: { paddingTop: 20, paddingBottom: 0 } }}
        >
            <style>{`
                .ant-form-item-explain-error {
                    font-size: 12px !important;
                }
            `}</style>
            <Form form={form} layout="vertical" requiredMark={false}>
                <CreateRoleModalBody style={{ overflowY: 'auto', maxHeight: '60vh', paddingLeft: '16px', paddingRight: '12px' }}>
                    {!isEdit && (
                        <div style={{ position: 'relative' }}>
                            <Form.Item
                                name="code"
                                label="Template Code (Unique identifier)"
                                rules={[{ required: true, message: 'Please enter a unique template code' }]}
                                style={{ marginBottom: '24px' }}
                            >
                                <Input
                                    placeholder="e.g. agile_sprints"
                                    size="large"
                                    disabled={isEdit && !isView}
                                    readOnly={isView}
                                    maxLength={20}
                                    onChange={(e) => setCodeLen(e.target.value.length)}
                                />
                            </Form.Item>
                            {!isView && (
                                <div style={{ position: 'absolute', right: 0, bottom: '-22px', fontSize: '12px', color: '#8c8c8c', fontFamily: 'var(--font-family-geist)' }}>
                                    {codeLen}/20
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ position: 'relative' }}>
                        <Form.Item
                            name="name"
                            label="Template Name"
                            rules={[{ required: true, message: 'Please enter a template name' }]}
                            style={{ marginBottom: '24px' }}
                        >
                            <Input
                                placeholder="e.g. Agile with Sprints"
                                size="large"
                                readOnly={isView}
                                maxLength={50}
                                onChange={(e) => setNameLen(e.target.value.length)}
                            />
                        </Form.Item>
                        {!isView && (
                            <div style={{ position: 'absolute', right: 0, bottom: '-22px', fontSize: '12px', color: '#8c8c8c', fontFamily: 'var(--font-family-geist)' }}>
                                {nameLen}/50
                            </div>
                        )}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Form.Item
                            name="description"
                            label="Description"
                            style={{ marginBottom: '24px' }}
                        >
                            <Input.TextArea
                                placeholder="Describe the template's workflow and features..."
                                rows={3}
                                readOnly={isView}
                                maxLength={100}
                                onChange={(e) => setDescLen(e.target.value.length)}
                            />
                        </Form.Item>
                        {!isView && (
                            <div style={{ position: 'absolute', right: 0, bottom: '-22px', fontSize: '12px', color: '#8c8c8c', fontFamily: 'var(--font-family-geist)' }}>
                                {descLen}/100
                            </div>
                        )}
                    </div>

                    {!isEdit && (
                        <>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ant-color-text-primary, #000)', marginTop: '24px', marginBottom: '8px' }}>
                                Capabilities
                            </div>
                            <Divider style={{ margin: '0 0 16px 0' }} />

                            <Container $display="flex" $flexDirection="column" $gap="14px" style={{ padding: '0 4px', marginBottom: '20px' }}>
                                <Container $display="flex" $justifyContent="space-between" $alignItems="center">
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Use Phases</div>
                                        <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Organize projects into sequential roadmap phases.</div>
                                    </div>
                                    <Form.Item name="usePhases" valuePropName="checked" noStyle>
                                        <Switch disabled={isView} />
                                    </Form.Item>
                                </Container>

                                <Container $display="flex" $justifyContent="space-between" $alignItems="center">
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Use Milestones</div>
                                        <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Track billing events and client deliverables.</div>
                                    </div>
                                    <Form.Item name="useMilestones" valuePropName="checked" noStyle>
                                        <Switch disabled={isView} />
                                    </Form.Item>
                                </Container>

                                <Container $display="flex" $justifyContent="space-between" $alignItems="center">
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Use Sprints</div>
                                        <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Enable timebox Sprints and agile backlogs.</div>
                                    </div>
                                    <Form.Item name="useSprints" valuePropName="checked" noStyle>
                                        <Switch disabled={isView} />
                                    </Form.Item>
                                </Container>

                                <Container $display="flex" $justifyContent="space-between" $alignItems="center">
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Use Tasks</div>
                                        <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Assign tasks, track status, and log employee hours.</div>
                                    </div>
                                    <Form.Item name="useTasks" valuePropName="checked" noStyle>
                                        <Switch disabled={isView} />
                                    </Form.Item>
                                </Container>

                                <Container $display="flex" $justifyContent="space-between" $alignItems="center">
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Use Submission Memo</div>
                                        <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Attach formal delivery documentation to client review requests.</div>
                                    </div>
                                    <Form.Item name="useSubmissionMemo" valuePropName="checked" noStyle>
                                        <Switch disabled={isView} />
                                    </Form.Item>
                                </Container>
                            </Container>
                        </>
                    )}

                    {!isEdit && (
                        <>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ant-color-text-primary, #000)', marginTop: '24px', marginBottom: '8px' }}>
                                Advanced Settings
                            </div>
                            <Divider style={{ margin: '0 0 16px 0' }} />

                            <Form.Item name="submissionMode" label="Submission Review Mode">
                                {isView ? (
                                    <Input value={submissionLabel} readOnly size="large" />
                                ) : (
                                    <Select
                                        size="large"
                                        options={[
                                            { value: 'formal', label: 'Formal Client Submission' },
                                            { value: 'memo', label: 'Client Review Memo' },
                                            { value: 'internal', label: 'Internal Workspace Approval' },
                                            { value: 'none', label: 'No Client Approval' },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                        </>
                    )}

                    {isView ? (
                        <Form.Item name="allowedProjectTypes" label="Allowed Project Types">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                                {selectedTypes.length === 0 ? (
                                    <span style={{ fontSize: '13px', color: 'var(--ant-color-text-secondary)' }}>All project types allowed</span>
                                ) : (
                                    selectedTypes.map(t => {
                                        const opt = PROJECT_TYPE_OPTIONS.find(o => o.value === t);
                                        return (
                                            <StatusBadge key={t} status="primary">
                                                {opt ? opt.label : t}
                                            </StatusBadge>
                                        );
                                    })
                                )}
                            </div>
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item name="allowedProjectTypes" label="Allowed Project Types">
                                <Select
                                    mode="multiple"
                                    size="large"
                                    placeholder="Select allowed project types"
                                    tagRender={() => <></>}
                                    open={dropdownOpen}
                                    onDropdownVisibleChange={setDropdownOpen}
                                    onSelect={() => setDropdownOpen(false)}
                                    options={PROJECT_TYPE_OPTIONS.filter(o => !selectedTypes.includes(o.value))}
                                    allowClear
                                />
                            </Form.Item>
                            {selectedTypes.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px', marginBottom: '16px' }}>
                                    {selectedTypes.map(t => {
                                        const opt = PROJECT_TYPE_OPTIONS.find(o => o.value === t);
                                        return (
                                            <StatusBadge
                                                key={t}
                                                status="primary"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                            >
                                                {opt ? opt.label : t}
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const next = selectedTypes.filter(x => x !== t);
                                                        form.setFieldsValue({ allowedProjectTypes: next });
                                                    }}
                                                    style={{ cursor: 'pointer', opacity: 0.8, fontSize: 10, fontWeight: 'bold' }}
                                                >
                                                    ✕
                                                </span>
                                            </StatusBadge>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </CreateRoleModalBody>

                <CreateRoleModalFooter>
                    <div style={{ flex: 1 }} />
                    <Space size={12}>
                        {isView ? (
                            <Button type="button" $primary $blackOnLight onClick={onCancel}>
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button type="button" $primary={false} onClick={onCancel} disabled={isSaving}>
                                    Cancel
                                </Button>
                                <Button type="button" $primary $blackOnLight onClick={onSubmit} loading={isSaving}>
                                    {isEdit ? 'Save Changes' : 'Create Template'}
                                </Button>
                            </>
                        )}
                    </Space>
                </CreateRoleModalFooter>
            </Form>
        </AppModal>
    );
}
